import type { Task } from "@/contexts/TaskContext";

/**
 * Determines whether a task has enough content to be rendered.
 * Used to prevent blank/broken cards when a task has an empty `question`
 * but still has valid instruction, images, or interactive UI.
 */

// Subtypes that have their own dedicated UI and don't require a text question
const SELF_CONTAINED_SUBTYPES = new Set([
  "Tabelka",
  "Prawda/Fałsz",
  "AB/123",
  "Podkreślanka",
  "Multi-tabelka",
]);

const stripHtml = (html: string): string =>
  html.replace(/<[^>]*>/g, "").trim();

export function isTaskRenderable(task: Task | null | undefined): boolean {
  if (!task) return false;

  // 1. Has a meaningful text question
  if (task.question && stripHtml(task.question).length > 0) return true;

  // 2. Has instruction text
  if (task.instruction && task.instruction.trim().length > 0) return true;

  // 3. Has images (question images or instruction image)
  if (task.imageUrls && task.imageUrls.length > 0) return true;
  if (task.instructionImageUrl) return true;

  // 4. Has a self-contained subtype with dedicated UI
  if (task.taskSubtype && SELF_CONTAINED_SUBTYPES.has(task.taskSubtype)) return true;

  // 5. Has table data or matching data
  if (task.tableData) return true;
  if (task.matchingData) return true;
  if (task.multiTableData && task.multiTableData.length > 0) return true;

  // 6. Has strikethrough text (Podkreślanka)
  if (task.strikethroughText && task.strikethroughText.trim().length > 0) return true;

  return false;
}
